import classNames from "classnames";
import { BuildNode, MenuLink, MenuNode, NodeStates } from "../../build/types";
import { BuilderMenuProps } from "../BuilderInterface";

import DynamicIcon from "../DynamicIcon";
import {
    getColorsFromInteractiveStates,
    getCompoundStyle,
    getStateColorsForBuilder,
    getStateStyleForBuilder,
    getStylesFromInteractiveStates
} from "../helpers";
import "./style.scss";
import { useInteractionStates } from "../../hooks/useInteractionStates";

interface MenuItemProps {
    node: BuildNode;
    linkIndex: number;
    builder?: boolean;
    nodeState?: NodeStates;
    onItemClick?: (link: string) => void;
    currentActivePageId?: string;
    disabled?: boolean;
}

const MenuItem = ({ node, linkIndex, builder, nodeState, onItemClick, currentActivePageId, disabled }: MenuItemProps) => {
    const { setElement, states } = useInteractionStates();
    const { stateManager, menu } = node as MenuNode;
    const link = menu.links[linkIndex];
    const { itemLayout } = menu;

    const handleClick = () => {
        if (onItemClick) {
            onItemClick?.(link.link);
        }
    };

    if (link) {
        const { icon, name, typography } = link;

        if (!link.icon && !link.name) return <div className="empty-menu-item"></div>;

        let iconElem = null;

        if (builder) {
            const wrapperStyle = getCompoundStyle({
                otherStyle: itemLayout,
                stateStyle: getStateStyleForBuilder(stateManager, nodeState, "menu_itemLayout")
            });

            if (icon?.src) {
                const iconStyle = getCompoundStyle({
                    otherStyle: icon.style,
                    stateStyle: getStateStyleForBuilder(stateManager, nodeState, `menu_links[${linkIndex}]_icon_style`)
                });
                const iconColors =
                    getStateColorsForBuilder(stateManager, nodeState, `menu_links[${linkIndex}]_icon_colors`) || icon.colors;
                iconElem = <DynamicIcon name={icon.src} style={iconStyle} colors={iconColors} />;
            }

            const nameStyle = getCompoundStyle({
                otherStyle: typography,
                stateStyle: getStateStyleForBuilder(stateManager, nodeState, `menu_links[${linkIndex}]_typography`)
            });

            return (
                <div className="builder-menu-link" style={wrapperStyle}>
                    <div className="icon">{iconElem}</div>
                    {name && <p style={nameStyle}>{name}</p>}
                </div>
            );
        } else {
            const extraStates = (
                disabled || currentActivePageId === link.link
                    ? [currentActivePageId === link.link && NodeStates.SELECTED, disabled && NodeStates.DISABLED].filter(Boolean)
                    : undefined
            ) as NodeStates[];

            const wrapperStyle = getStylesFromInteractiveStates({
                states,
                defaultStyle: itemLayout,
                stateManager,
                paths: ["menu_itemLayout"],
                extraStates
            });

            if (icon?.src) {
                const iconStyle = getStylesFromInteractiveStates({
                    states,
                    defaultStyle: icon.style,
                    stateManager,
                    paths: [`menu_links[${linkIndex}]_icon_style`],
                    extraStates
                });
                const iconColors = getColorsFromInteractiveStates({
                    states,
                    stateManager,
                    paths: [`menu_links[${linkIndex}]_icon_colors`],
                    defaultColors: icon.colors,
                    extraStates
                });
                iconElem = <DynamicIcon name={icon.src} style={iconStyle} colors={iconColors} />;
            }

            const nameStyle = getStylesFromInteractiveStates({
                states,
                defaultStyle: typography,
                stateManager,
                paths: [`menu_links[${linkIndex}]_typography`],
                extraStates
            });

            return (
                <div className="builder-menu-link" style={wrapperStyle} ref={setElement} onClick={handleClick}>
                    <div className="icon">{iconElem}</div>
                    {name && <p style={nameStyle}>{name}</p>}
                </div>
            );
        }
    }
};

const BuilderMenu = ({ node, builderProps, onItemClick, currentActivePageId, disabled }: BuilderMenuProps) => {
    const { id, attr, menu, stateManager } = node as MenuNode;
    const { states, setElement } = useInteractionStates();
    const { links } = menu;

    const menuWrapperClasses = classNames("builder-menu-element-wrapper", {
        [`${attr?.className}`]: !!attr?.className
    });

    if (builderProps) {
        const { nodeState, isContainer, selectCallback, outlineCallback, onDragStart, onDragEnd } = builderProps;

        const wrapperStyle = getCompoundStyle({
            node,
            isContainer,
            stateStyle: getStateStyleForBuilder(stateManager, nodeState, "attr_style"),
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        return (
            <div
                {...attr}
                className={links.length ? menuWrapperClasses : "empty-menu-element"}
                style={wrapperStyle}
                onClick={e => (selectCallback ? selectCallback(e, id) : {})}
                onMouseMove={e => (outlineCallback ? outlineCallback(e, id) : {})}
                id={id}
                onDragStart={e => onDragStart?.(e, id)}
                draggable={"true"}
                onDragEnd={onDragEnd}
            >
                {links.map((link: MenuLink, index: number) => (
                    <MenuItem node={node} linkIndex={index} key={link.id} builder nodeState={nodeState} />
                ))}
            </div>
        );
    } else {
        const wrapperStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: getCompoundStyle({ node }),
            stateManager,
            paths: ["attr_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        return (
            <div {...attr} className={menuWrapperClasses} style={wrapperStyle} ref={setElement}>
                {links.map((link: MenuLink, index: number) => (
                    <MenuItem
                        node={node}
                        linkIndex={index}
                        key={link.id}
                        onItemClick={onItemClick}
                        currentActivePageId={currentActivePageId}
                        disabled={disabled}
                    />
                ))}
            </div>
        );
    }
};
export default BuilderMenu;
